import { useQuery } from '@apollo/client/react';
import { useCallback } from 'react';
import { GET_VIEWER } from '@/graphql/queries/viewer';
import type { ViewerData, ViewerUser, WorkspaceData } from '@/shared/types/workspace';

export type UseViewerResult = {
  viewer: ViewerData | null;
  user: ViewerUser | null;
  workspace: WorkspaceData | null;
  isLoading: boolean;
  error: Error | undefined;
  refetch: () => Promise<unknown>;
  /** Asks the server again and returns the fresh viewer (null when signed out). */
  refreshViewer: () => Promise<ViewerData | null>;
};

/** The signed-in person and their workspace; `viewer` is null when signed out. */
export function useViewer(): UseViewerResult {
  const { data, previousData, loading, error, refetch } = useQuery(GET_VIEWER, {
    fetchPolicy: 'cache-and-network',
    context: { skipGlobalErrorToast: true },
  });
  const viewer = (data ?? previousData)?.viewer ?? null;
  const refreshViewer = useCallback(async () => {
    const result = await refetch();
    return result.data?.viewer ?? null;
  }, [refetch]);
  return {
    viewer,
    user: viewer?.user ?? null,
    workspace: viewer?.workspace ?? null,
    isLoading: loading && !data && !previousData,
    error: data || previousData ? undefined : error,
    refetch,
    refreshViewer,
  };
}
